import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraEntityCell, SpecraInlineBanner, SpecraInput, SpecraSegmentedControl } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { EmptyState } from '../../../components/layout';
import { useDebounce } from '../../../hooks/useDebounce';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { RegistryListNote } from '../../Registries/list/RegistryListNote';
import { OpenFullPage, useRowOpen } from '../../Registries/list/RowOpen';
import { useGovOrgSummaries, useReferencedGovOrgs } from '../../Registries/list/useRegistrySummaries';
import { useShowMore } from '../../Registries/list/useShowMore';
import { MergedRecordBanner } from '../../Registries/merge/MergedRecordBanner';
import { MergeRecordDialog } from '../../Registries/merge/MergeRecordDialog';
import { useGoToMergedSurvivor, useMergedRecordRedirect } from '../../Registries/merge/useMerge';
import { useRegistryRecordsEnabled } from '../../Registries/registryFlags';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { useAddGovernmentOffice, useDeleteGovernmentOrganization, useGovernmentOrganizations, useGovernmentOrgCatalog, useUpdateGovernmentOrganization } from '../Programs/referenceDataHooks';
import { GovernmentOrgDetailPanel } from './GovernmentOrgDetailPanel';
import { ancestorChain, childrenOf, govOrgErrorMessage, isSystemOrg } from './governmentOrgHierarchy';
import { RecordUnavailable } from './RecordUnavailable';
import { useRegistryQuery, useRegistryRecord } from './registryParams';
const NAME_FIELDS = [
    {
        kind: 'text',
        name: 'name',
        label: 'Name',
        required: true,
        maxLength: 200
    },
    {
        kind: 'text',
        name: 'acronym',
        label: 'Acronym',
        maxLength: 50
    }
];
// Office code and Location belong to tenant rows only (the shared reference is
// never edited, and the sub-org dialog under it carries just the child's name).
const TENANT_FIELDS = [
    ...NAME_FIELDS,
    {
        kind: 'text',
        name: 'officeCode',
        label: 'Office code',
        maxLength: 6,
        helpText: 'Six characters, e.g. 36C10B. Saved in uppercase; unique within your registry.',
        testId: Testids.deliveryRegistries.governmentOrgOfficeCode
    },
    {
        kind: 'text',
        name: 'locationText',
        label: 'Location',
        maxLength: 200,
        testId: Testids.deliveryRegistries.governmentOrgLocation
    }
];
const COLUMNS = [
    {
        key: 'name',
        header: 'Name'
    },
    {
        key: 'acronym',
        header: 'Acronym',
        render: (o)=>o.acronym || '—'
    },
    {
        key: 'orgType',
        header: 'Type',
        render: (o)=>o.orgType || '—'
    },
    {
        key: 'path',
        header: 'Path',
        render: (o)=>o.displayPath || '—'
    }
];
const ORG_MERGE_FIELDS = [
    {
        id: 'acronym',
        label: 'Acronym'
    },
    {
        id: 'path',
        label: 'Hierarchy'
    }
];
const orgMergeSide = (o)=>({
        id: o.id,
        name: o.name,
        fields: {
            acronym: o.acronym,
            path: o.displayPath
        },
        reference: isSystemOrg(o)
    });
export function GovernmentOrgsTab() {
    const recordsEnabled = useRegistryRecordsEnabled();
    const [mode, setMode] = useState('top');
    const referencedMode = recordsEnabled && mode === 'referenced';
    const referenced = useReferencedGovOrgs(referencedMode);
    const rowOpen = useRowOpen('GOVERNMENT_ORG');
    const [search, setSearch] = useRegistryQuery();
    const debouncedSearch = useDebounce(search, 300);
    // Roots (departments/agencies) by default; a search fans out across the
    // whole hierarchy server-side — same shape the program org picker uses.
    const orgsQuery = useGovernmentOrganizations({
        rootsOnly: true,
        search: debouncedSearch
    });
    const listedOrgs = orgsQuery.data ?? [];
    const canEdit = useTenantAreas().canManageCompanySettings;
    const catalog = useGovernmentOrgCatalog();
    const referencedRows = (referenced.data ?? []).map((s)=>catalog.byId.get(s.orgId) ?? {
            id: s.orgId,
            name: s.name,
            acronym: s.acronym,
            orgType: s.orgType,
            parentId: '',
            level: 0,
            displayPath: '',
            officeCode: s.officeCode,
            companyId: s.readOnlyReference ? '_system' : undefined
        });
    const orgs = referencedMode ? referencedRows.filter((o)=>!debouncedSearch || [
            o.name,
            o.acronym
        ].some((v)=>v.toLowerCase().includes(debouncedSearch.toLowerCase()))) : listedOrgs;
    const window = useShowMore(orgs, `${mode}:${debouncedSearch}`);
    const visible = recordsEnabled ? window.visible : orgs;
    const summaries = useGovOrgSummaries(recordsEnabled && !referencedMode ? visible.map((o)=>o.id) : []);
    const summaryRows = referencedMode ? referenced.data : summaries.data;
    const summaryError = referencedMode ? referenced.isError : summaries.isError;
    const summaryById = new Map((summaryError ? [] : summaryRows ?? []).map((s)=>[
            s.orgId,
            s
        ]));
    const columns = [
        {
            key: 'name',
            header: 'Office',
            render: (o)=>{
                const type = summaryById.get(o.id)?.displayType || o.orgType;
                const label = {
                    DEPARTMENT: 'Department',
                    COMPONENT: 'Component',
                    CONTRACTING_OFFICE: 'Contracting office',
                    OFFICE: 'Office',
                    OTHER: 'Other'
                }[type] || type;
                return /*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: o.name,
                    description: [
                        o.acronym,
                        label
                    ].filter(Boolean).join(' · ')
                });
            }
        },
        {
            key: 'parent',
            header: 'Part of',
            render: (o)=>summaryById.get(o.id)?.topAncestor?.name || '—'
        },
        {
            key: 'vehicles',
            header: 'Buys through',
            render: (o)=>{
                const s = summaryById.get(o.id);
                return s?.vehiclesAwarded.map((v)=>v.name).join(', ') || s?.vehiclesUsedVia.map((v)=>v.viaOrg?.name ? `via ${v.viaOrg.name}` : '').filter(Boolean).join(', ') || '—';
            }
        },
        {
            key: 'contracts',
            header: 'Our contracts',
            render: (o)=>{
                const s = summaryById.get(o.id);
                if (!s?.visiblePrograms.length) return '—';
                const extra = Math.max(0, s.visibleProgramCount - s.visiblePrograms.length);
                return s.visiblePrograms.map((p)=>p.name).join(', ') + (extra ? ` +${extra}` : '');
            }
        },
        {
            key: 'people',
            header: 'People we know',
            align: 'right',
            render: (o)=>summaryById.get(o.id)?.knownPeopleCount ?? '—'
        },
        {
            key: 'notices',
            header: 'Notices',
            render: ()=>'—'
        }
    ];
    const addOrg = useAddGovernmentOffice();
    const updateOrg = useUpdateGovernmentOrganization();
    const deleteOrg = useDeleteGovernmentOrganization();
    const { recordId: selectedId, openRecord, closeRecord } = useRegistryRecord();
    const [dialog, setDialog] = useState(null);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [error, setError] = useState(null);
    // Refusals of the dialog's own submit render inside the dialog, which stays open.
    const [dialogError, setDialogError] = useState(null);
    // The catalog resolves ancestors and children; a search hit it has not
    // loaded yet still opens from the row that was clicked.
    const byId = useMemo(()=>{
        const m = new Map(catalog.byId);
        for (const o of orgs){
            m.set(o.id, m.get(o.id) ?? o);
        }
        return m;
    }, [
        catalog.byId,
        orgs
    ]);
    const selected = selectedId ? byId.get(selectedId) : undefined;
    // An id that resolves to nothing (unknown, foreign tenant) shows the generic
    // unavailable state once both lookups have settled.
    const missing = !!selectedId && !selected && !catalog.isLoading && !orgsQuery.isLoading;
    // A loser's URL lands on its survivor; only a record with no merge pointer is "unavailable".
    const redirect = useMergedRecordRedirect('GOVERNMENT_ORG', selectedId, missing);
    const recordUnavailable = missing && !redirect.resolving;
    const goToSurvivor = useGoToMergedSurvivor();
    const [merging, setMerging] = useState(false);
    const ancestors = useMemo(()=>selected ? ancestorChain(selected, byId) : [], [
        selected,
        byId
    ]);
    const suborgs = useMemo(()=>selected ? childrenOf(catalog.data ?? [], selected.id) : [], [
        selected,
        catalog.data
    ]);
    const select = (id)=>{
        setError(null);
        if (id) openRecord(id);
        else closeRecord();
    };
    const run = async (action, after)=>{
        setError(null);
        try {
            await action();
            after?.();
        } catch (err) {
            setError(govOrgErrorMessage(err));
        } finally{
            setDialog(null);
            setConfirmingDelete(false);
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            recordsEnabled && /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "Show",
                size: "sm",
                testId: Testids.deliveryRegistries.govOrgsShow,
                value: mode,
                onValueChange: setMode,
                options: [
                    {
                        value: 'top',
                        label: 'Top level'
                    },
                    {
                        value: 'referenced',
                        label: 'Referenced in our work'
                    }
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraInput, {
                "data-testid": Testids.deliveryRegistries.governmentOrgSearchInput,
                placeholder: "Search agencies and offices by name",
                value: search,
                onChange: (e)=>setSearch(e.target.value),
                className: "max-w-sm"
            }),
            recordUnavailable && /*#__PURE__*/ _jsx(RecordUnavailable, {
                testId: Testids.deliveryRegistries.recordUnavailable,
                onDismiss: closeRecord
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: visible,
                columns: recordsEnabled ? columns : COLUMNS,
                testid: Testids.deliveryRegistries.governmentOrgsTable,
                rowTestid: Testids.deliveryRegistries.governmentOrgRow,
                rowKey: (o)=>o.id,
                isLoading: referencedMode ? referenced.isLoading : orgsQuery.isLoading,
                error: referencedMode ? undefined : orgsQuery.error,
                onRetry: orgsQuery.refetch,
                hideRowActions: !recordsEnabled,
                rowActions: (row)=>rowOpen.quickLookAction(row.id),
                onRowClick: (row)=>recordsEnabled ? rowOpen.onRowClick(row.id) : select(row.id),
                emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                    title: "No government organizations",
                    description: debouncedSearch ? 'No agencies or offices match that search.' : 'Government organizations appear here once reference data is loaded.'
                })
            }),
            recordsEnabled && window.hasMore && /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                "data-testid": Testids.deliveryRegistries.showMore,
                onClick: window.showMore,
                children: "Show more"
            }),
            recordsEnabled && /*#__PURE__*/ _jsx(RegistryListNote, {
                children: "Seeded from the federal hierarchy. Read-only reference rows that everything else links to."
            }),
            selected && /*#__PURE__*/ _jsx(GovernmentOrgDetailPanel, {
                org: selected,
                headerAction: /*#__PURE__*/ _jsx(OpenFullPage, {
                    kind: "GOVERNMENT_ORG",
                    id: selected.id
                }),
                ancestors: ancestors,
                suborgs: suborgs,
                canEdit: canEdit,
                error: error,
                onSelect: select,
                onAddChild: ()=>setDialog('add'),
                onEdit: ()=>setDialog('edit'),
                onDelete: ()=>setConfirmingDelete(true),
                onMerge: ()=>setMerging(true)
            }),
            selected && /*#__PURE__*/ _jsx(MergedRecordBanner, {
                kind: "GOVERNMENT_ORG",
                survivorId: selected.id
            }),
            merging && selected && /*#__PURE__*/ _jsx(MergeRecordDialog, {
                kind: "GOVERNMENT_ORG",
                open: true,
                onOpenChange: setMerging,
                loser: orgMergeSide(selected),
                candidates: (catalog.data ?? []).filter((o)=>o.id !== selected.id).map(orgMergeSide),
                fieldDefs: ORG_MERGE_FIELDS,
                onMerged: goToSurvivor
            }),
            selected && /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: dialog !== null,
                onOpenChange: (open)=>{
                    if (!open) {
                        setDialog(null);
                        setDialogError(null);
                    }
                },
                // A shared reference row is never edited: its dialog only adds a child.
                title: isSystemOrg(selected) ? `Add a sub-organization under ${selected.name}` : dialog === 'edit' ? `Edit ${selected.name}` : `Add suborganization under ${selected.name}`,
                fields: isSystemOrg(selected) ? NAME_FIELDS : TENANT_FIELDS,
                defaultValues: dialog === 'edit' && !isSystemOrg(selected) ? {
                    name: selected.name,
                    acronym: selected.acronym,
                    officeCode: selected.officeCode ?? '',
                    locationText: selected.locationText ?? ''
                } : {
                    name: '',
                    acronym: '',
                    officeCode: '',
                    locationText: ''
                },
                isSubmitting: addOrg.isPending || updateOrg.isPending,
                submitLabel: dialog === 'edit' && !isSystemOrg(selected) ? 'Save changes' : 'Add suborganization',
                extraContent: dialogError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    "data-testid": Testids.deliveryRegistries.governmentOrgFormError,
                    children: dialogError
                }) : null,
                onSubmit: async (v)=>{
                    setDialogError(null);
                    const officeCode = (v.officeCode ?? '').trim().toUpperCase();
                    const locationText = (v.locationText ?? '').trim();
                    try {
                        if (dialog === 'edit' && !isSystemOrg(selected)) {
                            await updateOrg.mutateAsync({
                                org: selected,
                                name: v.name,
                                acronym: v.acronym,
                                // Only what the user changed; unsent = unchanged server-side.
                                ...officeCode !== (selected.officeCode ?? '') && {
                                    officeCode
                                },
                                ...locationText !== (selected.locationText ?? '') && {
                                    locationText
                                }
                            });
                        } else {
                            await addOrg.mutateAsync({
                                parent: selected,
                                name: v.name,
                                acronym: v.acronym,
                                ...officeCode && {
                                    officeCode
                                },
                                ...locationText && {
                                    locationText
                                }
                            });
                        }
                        setError(null);
                        setDialog(null);
                    } catch (err) {
                        setDialogError(govOrgErrorMessage(err));
                    }
                }
            }),
            selected && /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: confirmingDelete,
                onOpenChange: setConfirmingDelete,
                title: `Delete ${selected.name}?`,
                description: "This removes the organization from your registry.",
                isPending: deleteOrg.isPending,
                onConfirm: ()=>run(()=>deleteOrg.mutateAsync(selected.id), ()=>select(selected.parentId || null))
            })
        ]
    });
}
